import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, Segmented, TableSkeleton } from "../../../components/ui";
import { getQuizRanking, getQuizRewards, getQuizStats } from "../../../services/quizAdmin";
import { can } from "../../../utils/roles";
import RewardModal from "./RewardModal";
import "../payments-ledger/payments.css";
import "./quiz.css";

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const day = (d, opts = { day: "numeric", month: "short" }) => (d ? new Date(d).toLocaleDateString([], { ...opts, timeZone: "UTC" }) : "—");
const when = (d) => (d ? new Date(d).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

/** Reward badges of a player (Premium days, gift, message). */
const RewardBadges = ({ rewards }) => {
  const { t } = useTranslation();
  if (!rewards?.length) return <span className="ui-cell-muted">—</span>;
  return (
    <span className="quiz-badges">
      {rewards.map((r) => (
        <Badge key={r.id} color={r.kind === "premium" ? "amber" : r.kind === "gift" ? "violet" : r.tone === "encourage" ? "blue" : "green"}>
          {r.kind === "premium" ? `${r.premiumDays} ${t("days")} Premium` : r.kind === "gift" ? r.giftLabel : r.tone === "encourage" ? t("Encouraged") : t("Congratulated")}
        </Badge>
      ))}
    </span>
  );
};

/** Ranking of a week (Monday → Sunday), with rewards and messages. */
const Ranking = ({ canEdit }) => {
  const { t } = useTranslation();
  const [week, setWeek] = useState(0);
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // { player, tone }

  const load = useCallback(() => {
    setData(null);
    getQuizRanking(week).then(setData);
  }, [week]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="ui-card">
      <div className="quiz-week">
        <IconButton icon="ri-arrow-left-s-line" label={t("Previous week")} onClick={() => setWeek(week - 1)} />
        <div className="quiz-week-label">
          <strong>
            {data ? `${t("Week of")} ${day(data.start)} – ${day(data.end, { day: "numeric", month: "short", year: "numeric" })}` : "…"}
          </strong>
          <span className="ui-media-meta">
            {week === 0 ? t("Current week") : week === -1 ? t("Last week") : t("{{count}} weeks ago", { count: -week })}
            {data ? ` · ${fmt(data.players)} ${t("players")} · ${fmt(data.games)} ${t("games")}` : ""}
          </span>
        </div>
        <IconButton icon="ri-arrow-right-s-line" label={t("Next week")} onClick={() => week < 0 && setWeek(week + 1)} />
      </div>

      {!canEdit && (
        <p className="quiz-readonly" role="note">
          <i className="ri-lock-line" aria-hidden="true"></i>
          {t("You can see the ranking. To reward players or send them emails, ask a super admin for full access to the « Game (quiz) » section.")}
        </p>
      )}

      {data?.items?.length > 0 && (
        <div className="quiz-podium">
          {data.items.slice(0, 3).map((p) => (
            <article key={p.user.id} className="quiz-podium-card" data-rank={p.rank}>
              <span className="quiz-rank" data-rank={p.rank}>
                {p.rank}
              </span>
              <h3>{p.user.name}</h3>
              <p className="ui-media-meta">
                {t("Average")} <strong>{String(p.average).replace(".", ",")}</strong> · {fmt(p.games)} {t("games")}
                {p.correctRate != null ? ` · ${p.correctRate} %` : ""}
              </p>
              <RewardBadges rewards={p.rewards} />
              {canEdit && (
                <div className="quiz-podium-actions">
                  <button type="button" className="ui-btn" onClick={() => setModal({ player: p, tone: "congrats" })}>
                    <i className="ri-award-line" aria-hidden="true"></i>
                    {t("Reward")}
                  </button>
                  <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setModal({ player: p, tone: "encourage" })}>
                    <i className="ri-heart-line" aria-hidden="true"></i>
                    {t("Encourage")}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {!data ? (
        <TableSkeleton />
      ) : !data.items.length ? (
        <EmptyState icon="ri-trophy-line">{t("Nobody played this week")}</EmptyState>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t("Player")}</th>
                <th title={t("Average of good answers per game: the ranking rule of the app")}>{t("Average")}</th>
                <th>{t("Games")}</th>
                <th>{t("Best")}</th>
                <th>{t("Good answers")}</th>
                <th>{t("Rewards and messages")}</th>
                <th>{canEdit ? t("Actions") : ""}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.user.id} data-podium={p.rank <= 3 || undefined}>
                  <td>
                    <span className="quiz-rank" data-rank={p.rank <= 3 ? p.rank : undefined}>
                      {p.rank}
                    </span>
                  </td>
                  <td>
                    <span className="ui-media-title">{p.user.name}</span>
                    <span className="ui-media-meta">
                      {p.user.email || p.user.phone || "—"} · {t("All time")} : {fmt(p.allTime.games)} {t("games")}
                    </span>
                  </td>
                  <td>
                    <strong>{String(p.average).replace(".", ",")}</strong>
                  </td>
                  <td>{fmt(p.games)}</td>
                  <td>{fmt(p.best)}</td>
                  <td>{p.correctRate == null ? "—" : `${p.correctRate} %`}</td>
                  <td>
                    <RewardBadges rewards={p.rewards} />
                  </td>
                  <td>
                    {canEdit && (
                      <div className="quiz-row-actions">
                        <button type="button" className="ui-btn quiz-btn-sm" onClick={() => setModal({ player: p, tone: "congrats" })} aria-label={`${t("Reward")} ${p.user.name}`}>
                          <i className="ri-award-line" aria-hidden="true"></i>
                          {t("Reward")}
                        </button>
                        <button type="button" className="ui-btn ui-btn-secondary quiz-btn-sm" onClick={() => setModal({ player: p, tone: "encourage" })} aria-label={`${t("Encourage")} ${p.user.name}`}>
                          <i className="ri-heart-line" aria-hidden="true"></i>
                          {t("Encourage")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="ui-hint quiz-hint">
        {t("Same rule as the app: average of good answers per game over the week, then the number of games. The rules reward the top 3 every week.")}
      </p>
      {modal && (
        <RewardModal
          player={modal.player}
          week={week}
          initialTone={modal.tone}
          onClose={() => setModal(null)}
          onSent={() => {
            setModal(null);
            load();
          }}
        />
      )}
    </section>
  );
};

const change = (current, previous) => {
  if (!previous) return null;
  const value = Math.round(((current - previous) / previous) * 100);
  return `${value > 0 ? "+" : ""}${value} %`;
};

/**
 * Games and players over the period: every day (days without games at 0), or
 * every week beyond 60 days. Bars for games, line for players.
 */
const activityChart = (data, t) => {
  const byDay = new Map(data.perDay.map((d) => [d.day, d]));
  const points = [];
  const start = new Date(data.start);
  const first = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()) + 864e5;
  for (let ms = first; ms <= new Date(data.end).getTime(); ms += 864e5) {
    const key = new Date(ms).toISOString().slice(0, 10);
    points.push({ x: ms, games: byDay.get(key)?.games || 0, players: byDay.get(key)?.players || 0 });
  }
  const weekly = data.period > 60;
  let rows = points;
  if (weekly) {
    const weeks = new Map();
    points.forEach((p) => {
      const d = new Date(p.x);
      const monday = p.x - ((d.getUTCDay() + 6) % 7) * 864e5;
      const w = weeks.get(monday) || { x: monday, games: 0, players: 0 };
      w.games += p.games;
      w.players = Math.max(w.players, p.players);
      weeks.set(monday, w);
    });
    rows = [...weeks.values()];
  }
  const muted = { colors: "#9b9ba6" };
  const label = (ms) => new Date(ms).toLocaleDateString([], { day: "numeric", month: "short", timeZone: "UTC" });
  return {
    weekly,
    type: "line",
    series: [
      { name: t("Games"), type: "column", data: rows.map((r) => ({ x: r.x, y: r.games })) },
      { name: weekly ? t("Players (best day)") : t("Players"), type: "line", data: rows.map((r) => ({ x: r.x, y: r.players })) },
    ],
    options: {
      chart: { id: "quiz-activity", background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", animations: { enabled: false } },
      colors: ["#1176BC", "#CDAE6E"],
      stroke: { width: [0, 3], curve: "smooth" },
      plotOptions: { bar: { columnWidth: rows.length > 40 ? "80%" : "55%", borderRadius: 3 } },
      dataLabels: { enabled: false },
      markers: { size: rows.length <= 14 ? 4 : 0, strokeWidth: 0 },
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      legend: { position: "top", horizontalAlign: "right", labels: muted },
      xaxis: { type: "datetime", labels: { style: muted, datetimeUTC: true, formatter: (value) => label(value) }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: (value) => Math.round(value).toLocaleString() } },
      tooltip: { theme: "dark", shared: true, intersect: false, x: { formatter: (value) => (weekly ? `${t("Week of")} ${label(value)}` : label(value)) } },
    },
  };
};

/** Game statistics over a period. */
const Stats = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    getQuizStats(days).then(setData);
  }, [days]);

  const c = data?.current;
  const p = data?.previous;
  const card = (value, label, delta) => (
    <div className="ui-card ledger-stat">
      <strong>{value ?? "—"}</strong>
      <span>
        {label}
        {delta && <em className="quiz-delta"> {delta}</em>}
      </span>
    </div>
  );
  const chart = data ? activityChart(data, t) : null;

  return (
    <>
      <div className="quiz-toolbar">
        <Segmented
          label={t("Period")}
          value={days}
          onChange={setDays}
          options={[
            { value: 7, label: t("7 days") },
            { value: 30, label: t("30 days") },
            { value: 90, label: t("90 days") },
            { value: 365, label: t("1 year") },
          ]}
        />
      </div>
      <div className="ledger-stats">
        {card(c ? fmt(c.games) : null, t("Games played"), c && change(c.games, p.games))}
        {card(c ? fmt(c.players) : null, t("Players"), c && change(c.players, p.players))}
        {card(c ? fmt(c.newPlayers) : null, t("New players"))}
        {card(c ? String(c.averageScore).replace(".", ",") : null, t("Average score"))}
        {card(c?.correctRate != null ? `${c.correctRate} %` : c ? "—" : null, t("Good answers"))}
        {card(c ? String(c.gamesPerPlayer).replace(".", ",") : null, t("Games per player"))}
      </div>

      <section className="ui-card">
        <h2 className="quiz-h2">{chart?.weekly ? t("Games and players per week") : t("Games and players per day")}</h2>
        {!data ? (
          <TableSkeleton rows={3} />
        ) : !data.perDay.length ? (
          <EmptyState icon="ri-bar-chart-line">{t("No game over this period")}</EmptyState>
        ) : (
          <Chart type={chart.type} height={300} options={chart.options} series={chart.series} />
        )}
      </section>

      <div className="quiz-columns">
        {[
          { key: "hardest", title: t("Most missed questions") },
          { key: "easiest", title: t("Most successful questions") },
        ].map((col) => (
          <section key={col.key} className="ui-card">
            <h2 className="quiz-h2">{col.title}</h2>
            {!data ? (
              <TableSkeleton rows={4} />
            ) : !data[col.key].length ? (
              <p className="ui-hint">{t("Not enough answers yet (5 per question at least).")}</p>
            ) : (
              <ul className="quiz-questions">
                {data[col.key].map((q) => (
                  <li key={q.id}>
                    <span className="quiz-q-rate" data-low={q.correctRate < 40 || undefined}>
                      {q.correctRate} %
                    </span>
                    <span>
                      <span className="ui-media-title">{q.text}</span>
                      <span className="ui-media-meta">
                        {[q.category, q.difficulty, `${fmt(q.asked)} ${t("answers")}`].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <section className="ui-card">
        <h2 className="quiz-h2">
          {t("Question bank")} {data ? `· ${fmt(data.bank.questions)} ${t("questions")}` : ""}
        </h2>
        {data && (
          <div className="quiz-cats">
            {data.bank.categories.map((cat) => (
              <span key={cat.id} className="quiz-cat" data-off={!cat.active || undefined}>
                {cat.name} <strong>{fmt(cat.questions)}</strong>
                {!cat.active && <em> · {t("inactive")}</em>}
              </span>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

/** Rewards and messages already sent. */
const History = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    getQuizRewards(page).then(setData);
  }, [page]);
  if (!data) return <TableSkeleton />;
  if (!data.items.length) return <EmptyState icon="ri-award-line">{t("No reward sent yet")}</EmptyState>;
  return (
    <section className="ui-card">
      <div className="ui-table-wrap">
        <table className="ui-table">
          <thead>
            <tr>
              <th>{t("Date")}</th>
              <th>{t("Player")}</th>
              <th>{t("Week")}</th>
              <th>{t("Rank")}</th>
              <th>{t("Reward / message")}</th>
              <th>{t("Sent")}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id}>
                <td className="ui-cell-muted">{when(r.at)}</td>
                <td>
                  <span className="ui-media-title">{r.user?.name || "—"}</span>
                  <span className="ui-media-meta">{r.user?.email}</span>
                </td>
                <td>{day(r.weekStart)}</td>
                <td>{r.rank || "—"}</td>
                <td>
                  <RewardBadges rewards={[r]} />
                </td>
                <td>
                  {r.emailSent ? <Badge color="green">{t("Email")}</Badge> : <Badge color="slate">{t("No email")}</Badge>}{" "}
                  {r.pushSent && <Badge color="blue">{t("Notification")}</Badge>}
                  {r.error && <span className="ui-media-meta">{r.error}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />
    </section>
  );
};

/** Quiz game: weekly ranking with rewards, statistics, history of rewards. */
const Quiz = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("ranking");
  const canEdit = can("quiz", "edit");
  return (
    <Page>
      <PageHeader title={t("Game (quiz)")} subtitle={t("Weekly ranking of the players, rewards and messages, game statistics.")} />
      <div className="quiz-tabs">
        <Segmented
          label={t("Section")}
          value={tab}
          onChange={setTab}
          options={[
            { value: "ranking", label: t("Weekly ranking") },
            { value: "stats", label: t("Statistics") },
            { value: "history", label: t("Rewards sent") },
          ]}
        />
      </div>
      {tab === "ranking" ? <Ranking canEdit={canEdit} /> : tab === "stats" ? <Stats /> : <History />}
    </Page>
  );
};

export default Quiz;
